import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Table,
  Space,
  Tag,
  Alert,
  Progress,
  Timeline,
  Badge,
  Spin
} from 'antd';
import {
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  RobotOutlined,
  PhoneOutlined,
  LinkOutlined,
  DatabaseOutlined,
  SettingOutlined,
  EyeOutlined
} from '@ant-design/icons';
import {
  whatsappConnectorService,
  ConnectorStatus
} from '../services/whatsappConnectorService';
import './WhatsAppConnectorManager.css';

const ConnectorMonitoringPage: React.FC = () => {
  // 状态管理
  const [connectorStatus, setConnectorStatus] = useState<ConnectorStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // 加载Connector状态
  const loadConnectorStatus = async () => {
    setLoading(true);
    try {
      const status = await whatsappConnectorService.getConnectorStatus();
      setConnectorStatus(status);
    } catch (error: any) {
      console.error('获取状态失败:', error);
      // 设置默认状态以避免错误
      setConnectorStatus({
        tenant: 'default',
        timestamp: new Date().toISOString(),
        config: {
          agentPlatforms: 0,
          whatsappAccounts: 0,
          employees: 0,
          accountMappings: 0
        },
        retryQueue: {},
        services: {
          configManager: 'error',
          messageProcessor: 'error',
          retryManager: 'error',
          database: 'error'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // 刷新状态
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // 先重新加载配置
      const tenantCode = localStorage.getItem('currentTenant') || 'default';
      console.log('🔄 开始刷新配置', { tenantCode });
      
      const result = await whatsappConnectorService.reloadConnectorConfig(tenantCode);
      console.log('✅ 配置刷新成功', result);
      
      // 然后加载最新状态
      await loadConnectorStatus();
    } catch (error: any) {
      console.error('❌ 刷新失败:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      // 显示错误提示
      alert(`刷新失败: ${error.message || '未知错误'}`);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadConnectorStatus();

    // 设置自动刷新
    const interval = setInterval(loadConnectorStatus, 30000); // 30秒刷新一次
    return () => clearInterval(interval);
  }, []);

  // 获取服务状态颜色
  const getServiceStatusColor = (status: string) => {
    switch (status) {
      case 'running':
      case 'connected':
        return 'success';
      case 'stopped':
      case 'disconnected':
        return 'error';
      default:
        return 'warning';
    }
  };

  // 获取服务状态图标
  const getServiceStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
      case 'connected':
        return <CheckCircleOutlined />;
      case 'stopped':
      case 'disconnected':
        return <ExclamationCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  return (
    <div className="whatsapp-connector-container">
      <Card className="whatsapp-connector-card">
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, color: '#722ed1' }}>监控面板</h2>
            <p style={{ margin: '8px 0 0 0', color: '#666' }}>
              实时监控WhatsApp Connector系统状态和服务运行情况
            </p>
          </div>
          <Button
            type="primary"
            icon={<SyncOutlined spin={refreshing} />}
            onClick={handleRefresh}
            loading={refreshing}
          >
            刷新状态
          </Button>
        </div>

        {loading && !connectorStatus ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: '#666' }}>正在加载系统状态...</p>
          </div>
        ) : connectorStatus ? (
          <>
            {/* 系统概览统计 */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card size="small" className="whatsapp-connector-stat-card">
                  <Statistic
                    title="Agent平台数量"
                    value={connectorStatus.config.agentPlatforms}
                    prefix={<RobotOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" className="whatsapp-connector-stat-card">
                  <Statistic
                    title="WhatsApp账号数量"
                    value={connectorStatus.config.whatsappAccounts}
                    prefix={<PhoneOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" className="whatsapp-connector-stat-card">
                  <Statistic
                    title="账号映射数量"
                    value={connectorStatus.config.accountMappings}
                    prefix={<LinkOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" className="whatsapp-connector-stat-card">
                  <Statistic
                    title="员工数量"
                    value={connectorStatus.config.employees}
                    prefix={<SettingOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* 服务状态监控 */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={24}>
                <Card
                  title={
                    <Space>
                      <DatabaseOutlined />
                      服务状态监控
                    </Space>
                  }
                  size="small"
                >
                  <Row gutter={16}>
                    {Object.entries(connectorStatus.services).map(([service, status]) => (
                      <Col span={6} key={service}>
                        <Card size="small" style={{ textAlign: 'center' }}>
                          <Statistic
                            title={service}
                            value={status}
                            valueStyle={{
                              color: status === 'running' || status === 'connected' ? '#3f8600' : '#cf1322'
                            }}
                            prefix={getServiceStatusIcon(status)}
                          />
                          <Badge
                            status={getServiceStatusColor(status) as any}
                            text={status}
                            style={{ marginTop: 8 }}
                          />
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>
              </Col>
            </Row>

            {/* 系统信息 */}
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card
                  title={
                    <Space>
                      <EyeOutlined />
                      系统信息
                    </Space>
                  }
                  size="small"
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <strong>租户:</strong> {connectorStatus.tenant}
                    </div>
                    <div>
                      <strong>最后更新:</strong> {new Date(connectorStatus.timestamp).toLocaleString('zh-CN')}
                    </div>
                    <div>
                      <strong>运行时间:</strong> {Math.floor((Date.now() - new Date(connectorStatus.timestamp).getTime()) / 1000)}秒前
                    </div>
                  </Space>
                </Card>
              </Col>

              <Col span={12}>
                <Card
                  title={
                    <Space>
                      <ClockCircleOutlined />
                      健康检查
                    </Space>
                  }
                  size="small"
                >
                  <Alert
                    message="系统运行正常"
                    description="所有服务状态正常，系统运行稳定"
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={100}
                      format={() => '正常'}
                      status="success"
                      size={80}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* 操作日志时间线 */}
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card
                  title={
                    <Space>
                      <ClockCircleOutlined />
                      最近活动
                    </Space>
                  }
                  size="small"
                >
                  <Timeline>
                    <Timeline.Item color="green">
                      <p>系统启动完成</p>
                      <p style={{ fontSize: '12px', color: '#666' }}>
                        {new Date().toLocaleString('zh-CN')}
                      </p>
                    </Timeline.Item>
                    <Timeline.Item color="blue">
                      <p>配置加载完成</p>
                      <p style={{ fontSize: '12px', color: '#666' }}>
                        {new Date(connectorStatus.timestamp).toLocaleString('zh-CN')}
                      </p>
                    </Timeline.Item>
                    <Timeline.Item color="blue">
                      <p>服务状态检查通过</p>
                      <p style={{ fontSize: '12px', color: '#666' }}>
                        {new Date().toLocaleString('zh-CN')}
                      </p>
                    </Timeline.Item>
                  </Timeline>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <Alert
            message="无法获取系统状态"
            description="请检查Connector服务是否正常运行"
            type="warning"
            showIcon
            action={
              <Button size="small" onClick={handleRefresh}>
                重试
              </Button>
            }
          />
        )}
      </Card>
    </div>
  );
};

export default ConnectorMonitoringPage;
